import type { MouseEvent } from 'react';

import type { CameraInfo } from '@scaleaq/sitecontrol-lib';

import { Tooltip } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import {
  StyledCameraNumberButton,
  StyledCameraSwitchPanel,
  StyledNextCameraButton,
} from '@/components/cameraStreamWidget/cameraSwitchPanel/cameraSwitchPanel.styles.ts';
import { Icon } from '@/utils';

type CameraSwitchPanelProps = {
  cameras: CameraInfo[];
  selectedCameraId: string | undefined;
  onSelectCamera: (camera: CameraInfo) => void;
  onNextCamera: () => void;
};

/**
 * Collapsed to the current camera number; hovering (or focusing) reveals every other camera in the
 * group and the cycle button. Numbers keep their own slot in group order whichever camera is
 * current, so the pill grows leftwards from the anchored right edge instead of reordering. All
 * cameras get a number, so groups larger than three still work.
 */
export const CameraSwitchPanel = ({
  cameras,
  selectedCameraId,
  onSelectCamera,
  onNextCamera,
}: CameraSwitchPanelProps) => {
  const { t: tCommon, i18n } = useTranslation();
  const { t } = useTranslation('landingPage');

  if (cameras.length < 2) return null;

  const cameraNumber = new Intl.NumberFormat(i18n.resolvedLanguage);
  const nextCameraLabel = tCommon('shortcuts.camera.nextCameraInGroup');
  const tooltipOffset = 7.5;
  const tooltipPlacement = 'top';
  return (
    <StyledCameraSwitchPanel>
      {cameras.map((camera, index) => {
        const isCurrent = camera.id === selectedCameraId;
        return (
          <Tooltip
            key={camera.id}
            tooltip={camera.name}
            placement={tooltipPlacement}
            offset={tooltipOffset}
          >
            <StyledCameraNumberButton
              type='button'
              variant={!isCurrent ? 'outline' : 'solid'}
              color={'action'}
              size='medium'
              label={cameraNumber.format(index + 1)}
              aria-label={isCurrent ? camera.name : t('selectCameraNamed', { name: camera.name })}
              aria-current={isCurrent || undefined}
              onClick={
                isCurrent
                  ? undefined
                  : (event: MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      onSelectCamera(camera);
                    }
              }
            />
          </Tooltip>
        );
      })}
      <Tooltip tooltip={nextCameraLabel} placement={tooltipPlacement} offset={tooltipOffset}>
        <StyledNextCameraButton
          type='button'
          variant='outline'
          color='action'
          size='large'
          Icon={<Icon.SwitchCamera size='large' />}
          aria-label={nextCameraLabel}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onNextCamera();
          }}
        />
      </Tooltip>
    </StyledCameraSwitchPanel>
  );
};
